import { Check, Clock, Circle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  deriveCommissionSteps,
  type CommissionStep,
  type CommissionStepsInput,
} from "@/lib/commission";
import { cn } from "@/lib/utils";

interface StepperProps extends CommissionStepsInput {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StepIcon({ state }: { state: CommissionStep["state"] }) {
  if (state === "complete") {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
        <Check className="size-4" aria-hidden />
      </div>
    );
  }
  if (state === "current") {
    return (
      <div className="relative flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-info bg-info-muted">
        <Clock className="size-3.5 text-info" aria-hidden />
        <span className="absolute inset-0 rounded-full animate-ping border-2 border-info opacity-30" />
      </div>
    );
  }
  if (state === "failed") {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive-muted text-destructive border border-destructive/20">
        <span className="text-base font-bold leading-none" aria-hidden>
          ×
        </span>
      </div>
    );
  }
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-border bg-background">
      <Circle className="size-3 text-muted-foreground/40" aria-hidden />
    </div>
  );
}

export function CommissionStepper(props: StepperProps) {
  const steps = deriveCommissionSteps(props);

  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[420px]">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-base">
            Referral progress: {props.displayName || props.inviteEmail}
          </SheetTitle>
        </SheetHeader>

        <ol
          className="mt-4 px-4"
          role="group"
          aria-label="Referral progress"
        >
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            return (
              <li
                key={i}
                className="flex gap-4"
                aria-current={step.state === "current" ? "step" : undefined}
              >
                {/* Icon + connector line */}
                <div className="flex flex-col items-center">
                  <StepIcon state={step.state} />
                  {!isLast && (
                    <div
                      className={cn(
                        "w-0.5 flex-1 my-1.5 min-h-[28px]",
                        step.state === "complete"
                          ? "bg-success/40"
                          : "bg-border",
                      )}
                    />
                  )}
                </div>

                {/* Label + description */}
                <div
                  className={cn("pb-8 min-w-0 flex-1 pt-1", isLast && "pb-2")}
                >
                  <p
                    className={cn(
                      "text-sm font-semibold leading-tight",
                      step.state === "failed" && "text-destructive",
                      step.state === "current" && "text-info",
                      step.state === "pending" && "text-muted-foreground/50",
                      step.state === "complete" && "text-foreground",
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-snug">
                    {step.description}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
